import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { PolicyImpactResults } from '../../services/policyImpactService';
import './EmissionsPerMileChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Match VehicleElectrificationPolicy constants so the chart reflects the same model
const EV_KWH_PER_MILE = 0.3;
const ICE_IMPROVEMENT_FACTOR = 0.95;
const BASELINE_GRID_INTENSITY = 0.2; // kg CO2/kWh - California 2023

const START_YEAR = 2005;
const PRESENT_YEAR = 2023; // policy applies from this year onward only
const END_YEAR = 2045;

export interface EmissionsPerMileChartProps {
  results: PolicyImpactResults | null;
  currentEVPercent: number;     // 2045 BAU EV% (slider minimum)
  currentEVPercent2023?: number; // present-year EV% (past ramp and start of future ramp)
  targetEVPercent: number;       // policy target EV% by 2045 (applies from present onward)
}

/**
 * Past (≤ present): EV% ramps 0 → evPresent. Future (> present): EV% ramps evPresent → endEV.
 * Used so "With policy" matches BAU until present, then diverges.
 */
function evPercentAtYear(year: number, evPresent: number, endEV: number): number {
  if (year <= START_YEAR) return 0;
  if (year <= PRESENT_YEAR) {
    const t = (year - START_YEAR) / (PRESENT_YEAR - START_YEAR);
    return evPresent * t;
  }
  if (year >= END_YEAR) return endEV;
  const t = (year - PRESENT_YEAR) / (END_YEAR - PRESENT_YEAR);
  return evPresent + (endEV - evPresent) * t;
}

/**
 * Chart of fleet-weighted GHG emissions per mile (kg CO2e/mile) over time (years).
 * X-axis = Year (like GHG forecast), Y-axis = kg CO₂e per mile.
 * Shows BAU vs With policy. Past (2005–present): same path for both. From present onward, policy ramps to target.
 */
const EmissionsPerMileChart: React.FC<EmissionsPerMileChartProps> = ({
  results,
  currentEVPercent,
  currentEVPercent2023,
  targetEVPercent,
}) => {
  const evPresent = currentEVPercent2023 ?? 0;

  const chartData = useMemo(() => {
    if (!results?.baseline?.vmt || results.baseline.vmt <= 0) {
      return {
        years: [] as number[],
        bauKgPerMile: [] as number[],
        policyKgPerMile: [] as number[],
        iceGhgPerMileKg: 0,
        evGhgPerMileKg: 0,
      };
    }
    const baselineLightDutyGHG = results.baseline.lightDutyGHG || 0;
    const baselineVMT = results.baseline.vmt;
    const baselineGhgPerMile = baselineLightDutyGHG / baselineVMT;
    const iceGhgPerMile = baselineGhgPerMile * ICE_IMPROVEMENT_FACTOR;
    const evGhgPerMileKg = BASELINE_GRID_INTENSITY * EV_KWH_PER_MILE;
    const iceGhgPerMileKg = iceGhgPerMile * 1000;

    const fleetKgPerMileAt = (evPct: number) => {
      const icePct = 100 - evPct;
      return (icePct / 100) * iceGhgPerMileKg + (evPct / 100) * evGhgPerMileKg;
    };

    const years: number[] = [];
    const bauKgPerMile: number[] = [];
    const policyKgPerMile: number[] = [];

    for (let y = START_YEAR; y <= END_YEAR; y++) {
      years.push(y);
      const bauEV = evPercentAtYear(y, evPresent, currentEVPercent);
      const policyEV = evPercentAtYear(y, evPresent, targetEVPercent);
      bauKgPerMile.push(fleetKgPerMileAt(bauEV));
      policyKgPerMile.push(fleetKgPerMileAt(policyEV));
    }

    return {
      years,
      bauKgPerMile,
      policyKgPerMile,
      iceGhgPerMileKg,
      evGhgPerMileKg,
    };
  }, [results, currentEVPercent, targetEVPercent, evPresent]);

  const { years, bauKgPerMile, policyKgPerMile, iceGhgPerMileKg, evGhgPerMileKg } = chartData;

  const data = useMemo(() => {
    if (years.length === 0) return null;
    return {
      labels: years.map((y) => String(y)),
      datasets: [
        {
          label: 'BAU',
          data: bauKgPerMile,
          borderColor: 'rgba(107, 114, 128, 1)',
          backgroundColor: 'rgba(107, 114, 128, 0.1)',
          borderWidth: 3,
          pointRadius: 0,
          fill: false,
          tension: 0.2,
          order: 2,
        },
        {
          label: 'With policy',
          data: policyKgPerMile,
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 4,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0.2,
          order: 1,
        },
      ],
    };
  }, [years, bauKgPerMile, policyKgPerMile]);

  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, bottom: 16, left: 60, right: 24 },
    },
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: 'Light-duty emissions per mile over time',
        font: { size: 16 },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toFixed(2)} kg CO₂e/mile`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Year' },
        grid: { display: true },
        offset: false,
      },
      y: {
        title: { display: true, text: 'kg CO₂e per mile' },
        min: 0,
        grid: { display: true },
      },
    },
  }), []);

  if (!results?.baseline?.vmt) {
    return (
      <div className="emissions-per-mile-chart-wrapper">
        <p className="emissions-per-mile-chart-placeholder">Load the policy tool to see emissions per mile over time.</p>
      </div>
    );
  }

  const bau2045 = bauKgPerMile[bauKgPerMile.length - 1];
  const policy2045 = policyKgPerMile[policyKgPerMile.length - 1];

  return (
    <div className="emissions-per-mile-chart-wrapper">
      <div className="emissions-per-mile-chart-container">
        {data && <Line data={data} options={options} />}
      </div>
      <div className="emissions-per-mile-chart-legend-inline">
        <span className="emissions-legend-item">
          <span className="emissions-legend-dot bau" /> BAU: {currentEVPercent}% EVs by 2045 → {bau2045?.toFixed(2) ?? '—'} kg CO₂e/mile
        </span>
        <span className="emissions-legend-item">
          <span className="emissions-legend-dot policy" /> With policy: {targetEVPercent}% EVs by 2045 → {policy2045?.toFixed(2) ?? '—'} kg CO₂e/mile
        </span>
      </div>
      <p className="emissions-per-mile-chart-methodology">
        Past (2005–{PRESENT_YEAR}): same path for both. Policy applies from {PRESENT_YEAR} onward (green line diverges). ICE: {iceGhgPerMileKg.toFixed(2)} kg CO₂e/mile; EV: {evGhgPerMileKg.toFixed(2)} kg CO₂e/mile.
      </p>
    </div>
  );
};

export default EmissionsPerMileChart;
